import { useState } from "react";
import LogoEditor from "./LogoEditor";
import CommunicationSettings from "./CommunicationSettings";
import StatusTagsEditor from "./StatusTagsEditor";
import { isFeatureEnabled, getFeatureVisibility, ColorPalettePanel } from "@/pages/dev-panel/page";
import { PREMIUM_POPUP_CONFIGS } from "@/components/feature/PremiumLockedButton";
import type { UserRole } from "@/utils/userStore";
import { siteData, GeneralSettings, defaultGeneralSettings } from "@/store/siteData";

// ── Inline Premium Ad Banner ─────────────────────────────────────────────
function InlinePremiumAd({ featureId }: { featureId: string }) {
  const cfg = PREMIUM_POPUP_CONFIGS[featureId];
  if (!cfg) return null;
  return (
    <div className="rounded-2xl overflow-hidden border border-amber-500/20">
      {/* Hero */}
      <div className="relative h-36 overflow-hidden">
        <img src={cfg.heroImageUrl} alt={cfg.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/50 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1020]/40 to-transparent"></div>
        <div className="absolute top-3 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 backdrop-blur-sm">
          <i className="ri-vip-crown-fill text-amber-400 text-xs"></i>
          <span className="text-amber-300 text-xs font-semibold">{cfg.badgeText}</span>
        </div>
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="text-white font-black text-lg">{cfg.title}</h3>
          <p className="text-[#A5B5CC] text-xs">{cfg.subtitle}</p>
        </div>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-3 border-t border-amber-500/15 bg-[#0A1020]">
        {cfg.stats.map((s, i) => (
          <div key={i} className={`py-2.5 text-center ${i < 2 ? "border-r border-amber-500/10" : ""}`}>
            <p className="font-black text-base" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[#5E7398] text-[9px] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
      {/* Description */}
      <div className="bg-[#0A1020] px-4 pt-3 pb-2">
        <p className="text-[#8BA0C0] text-xs leading-relaxed">{cfg.description}</p>
      </div>
      {/* Advantages */}
      <div className="bg-[#0A1020] px-4 pb-4 grid grid-cols-2 gap-2">
        {cfg.advantages.map((adv, i) => (
          <div key={i} className="flex items-start gap-2 p-2.5 rounded-xl bg-[#141E35] border border-[#2B3654]">
            <div className="w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: adv.color + "22" }}>
              <i className={`${adv.icon} text-xs`} style={{ color: adv.color }}></i>
            </div>
            <div className="min-w-0">
              <p className="text-white text-[11px] font-semibold">{adv.title}</p>
              <p className="text-[#5E7398] text-[10px] leading-tight mt-0.5">{adv.description}</p>
            </div>
          </div>
        ))}
      </div>
      {/* CTA */}
      <div className="mx-4 mb-4 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-500/20 flex-shrink-0">
          <i className="ri-lock-fill text-amber-400 text-sm"></i>
        </div>
        <p className="text-amber-300/90 text-xs leading-relaxed flex-1">{cfg.ctaText}</p>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", placeholder = "" }: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  type?: string;
  placeholder?: string;
}) => (
  <div>
    <label className="block text-[#7B8FAF] text-xs mb-2 uppercase tracking-widest">{label}</label>
    {type === "textarea" ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-4 py-3 outline-none focus:border-[#3B82F6] transition-colors resize-none placeholder-[#5E7398]"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-4 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]"
      />
    )}
  </div>
);

interface SiteSettingsProps {
  userRole?: UserRole;
}

type SectionId = "general" | "hero" | "social" | "logo" | "projects" | "communication" | "colors";

export default function SiteSettings({ userRole = "editor" }: SiteSettingsProps) {
  const isSupport = userRole === "support";
  const statusTagsVisibility  = getFeatureVisibility("status_tags");
  const colorPaletteVisibility = getFeatureVisibility("color_palette");

  const [settings, setSettings] = useState<GeneralSettings>(() => siteData.getGeneralSettings());
  const [heroSettings, setHeroSettings] = useState(() => siteData.getHero());
  const [ctaSettings, setCtaSettings] = useState(() => siteData.getCTA());
  
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId>(
    isSupport ? "communication" : "general"
  );

  const update = (key: keyof GeneralSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    await siteData.saveGeneralSettings(settings);
    await siteData.saveHero(heroSettings);
    await siteData.saveCTA(ctaSettings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = async () => {
    setSettings(defaultGeneralSettings);
    await siteData.saveGeneralSettings(defaultGeneralSettings);
    setSaved(false);
  };

  const allSections: { id: SectionId; label: string; icon: string }[] = [
    { id: "general",       label: "Şirket Bilgileri",          icon: "ri-building-line" },
    { id: "hero",          label: "Ana Sayfa Metinleri",        icon: "ri-layout-top-2-line" },
    { id: "logo",          label: "Logo Yönetimi",              icon: "ri-image-line" },
    ...(statusTagsVisibility !== "hidden" ? [{ id: "projects" as SectionId, label: "Proje Durum Etiketleri", icon: "ri-price-tag-3-line" }] : []),
    { id: "communication", label: "İletişim Widgetları",        icon: "ri-customer-service-2-line" },
    { id: "social",        label: "Sosyal Medya",               icon: "ri-share-circle-line" },
    ...(colorPaletteVisibility !== "hidden" ? [{ id: "colors" as SectionId, label: "Renk Paleti", icon: "ri-palette-line" }] : []),
  ];

  // Support rolü sadece iletişim widgetları sekmesini görür
  const sections = isSupport
    ? allSections.filter(s => s.id === "communication")
    : allSections;

  const showSaveBar = activeSection !== "logo" && activeSection !== "communication" && activeSection !== "projects" && activeSection !== "colors";

  return (
    <div className="space-y-6">
      {!isSupport && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Site Ayarları</h1>
            <p className="text-[#7B8FAF] text-sm mt-0.5">Şirket bilgileri ve içerik yönetimi</p>
          </div>
          {showSaveBar && (
            <div className="flex items-center gap-3">
              <button onClick={handleReset}
                className="px-4 py-2 text-sm text-[#7B8FAF] border border-[#2B3654] rounded-lg hover:border-red-500/40 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap">
                Sıfırla
              </button>
              <button onClick={handleSave}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
                </div>
                {saved ? "Kaydedildi!" : "Kaydet"}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        {!isSupport && (
          <div className="lg:col-span-1 space-y-1">
            {sections.map((sec) => (
              <button key={sec.id} onClick={() => setActiveSection(sec.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap text-left ${
                  activeSection === sec.id
                    ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40"
                    : "text-[#7B8FAF] hover:text-white hover:bg-white/5"
                }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${sec.icon} text-base`}></i>
                </div>
                {sec.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className={`${isSupport ? "lg:col-span-4" : "lg:col-span-3"} bg-[#141E35] border border-[#2B3654] rounded-xl p-6 space-y-5`}>
          {activeSection === "general" && (
            <>
              <h2 className="text-white font-semibold text-base pb-4 border-b border-[#2B3654]">Şirket Bilgileri</h2>
              <Field label="Şirket Adı" value={settings.companyName} onChange={v => update("companyName", v)} />
              <Field label="Slogan / Tagline" value={settings.tagline} onChange={v => update("tagline", v)} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Telefon" value={settings.phone} onChange={v => update("phone", v)} type="tel" />
                <Field label="E-posta" value={settings.email} onChange={v => update("email", v)} type="email" />
              </div>
              <Field label="Adres" value={settings.address} onChange={v => update("address", v)} type="textarea" />
              <Field label="Kuruluş Yılı" value={settings.foundedYear} onChange={v => update("foundedYear", v)} />
            </>
          )}

          {activeSection === "hero" && (
            <>
              <h2 className="text-white font-semibold text-base pb-4 border-b border-[#2B3654]">Ana Sayfa Metinleri</h2>
              <Field label="Hero Başlık" value={heroSettings.title} onChange={v => {setHeroSettings(p => ({ ...p, title: v })); setSaved(false);}} />
              <Field label="Hero Alt Metin" value={heroSettings.subtitle} onChange={v => {setHeroSettings(p => ({ ...p, subtitle: v })); setSaved(false);}} type="textarea" />
              <div className="h-px bg-[#2B3654] my-2"></div>
              <Field label="CTA Başlık" value={ctaSettings.title} onChange={v => {setCtaSettings(p => ({ ...p, title: v })); setSaved(false);}} />
              <Field label="CTA Alt Metin" value={ctaSettings.subtitle} onChange={v => {setCtaSettings(p => ({ ...p, subtitle: v })); setSaved(false);}} type="textarea" />
            </>
          )}

          {activeSection === "logo" && <LogoEditor />}
          {activeSection === "projects" && (
            statusTagsVisibility === "locked" ? (
              <InlinePremiumAd featureId="status_tags" />
            ) : (
              <StatusTagsEditor />
            )
          )}
          {activeSection === "communication" && <CommunicationSettings />}

          {/* Renk Paleti */}
          {activeSection === "colors" && (
            colorPaletteVisibility === "locked" ? (
              <InlinePremiumAd featureId="color_palette" />
            ) : (
              <ColorPalettePanel />
            )
          )}

          {activeSection === "social" && (
            <>
              <h2 className="text-white font-semibold text-base pb-4 border-b border-[#2B3654]">Sosyal Medya Hesapları</h2>
              <div className="space-y-4">
                {[
                  { label: "Instagram Kullanıcı Adı", fieldKey: "socialInstagram" as keyof GeneralSettings, icon: "ri-instagram-line", prefix: "@" },
                  { label: "LinkedIn Kullanıcı Adı", fieldKey: "socialLinkedin" as keyof GeneralSettings, icon: "ri-linkedin-box-line", prefix: "linkedin.com/company/" },
                  { label: "YouTube Kanal Adı", fieldKey: "socialYoutube" as keyof GeneralSettings, icon: "ri-youtube-line", prefix: "youtube.com/@" },
                ].map((item) => (
                  <div key={item.fieldKey}>
                    <label className="block text-[#7B8FAF] text-xs mb-2 uppercase tracking-widest">{item.label}</label>
                    <div className="flex items-center bg-[#0F1729] border border-[#2B3654] rounded-lg overflow-hidden focus-within:border-[#3B82F6] transition-colors">
                      <div className="w-10 h-10 flex items-center justify-center border-r border-[#2B3654] bg-[#141E35] flex-shrink-0">
                        <i className={`${item.icon} text-[#3B82F6]`}></i>
                      </div>
                      <span className="text-[#5E7398] text-sm px-2 whitespace-nowrap hidden sm:block">{item.prefix}</span>
                      <input type="text" value={settings[item.fieldKey]}
                        onChange={(e) => update(item.fieldKey, e.target.value)}
                        className="bg-transparent text-white text-sm flex-1 px-3 py-3 outline-none" />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {showSaveBar && (
            <div className="pt-4 border-t border-[#2B3654] flex justify-end">
              <button onClick={handleSave}
                className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
                </div>
                {saved ? "Kaydedildi!" : "Değişiklikleri Kaydet"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
